import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Grid,
    GridColumn as Column,
    GridToolbar,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { CommandCell } from '../../forms/CommandCell';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { AdministratorVenueEventAlertForm } from './VenueEventAlertForm';
import { AdministratorVenueEventAlertLogs } from './VenueEventAlertLogs';

export const AdministratorVenueEventAlerts = (props) => {
    const dispatch = useDispatch();

    const [venueEventAlertTypes, setVenueEventAlertTypes] = useState([]);
    const [ticketCountInputParameters, setTicketCountInputParameters] = useState([]);
    const [venueEventAlerts, setVenueEventAlerts] = useState([]);   
    const [areVenueEventAlertsLoading, setAreVenueEventAlertsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [openForm, setOpenForm] = useState(false);
    const [editItem, setEditItem] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);

    const [eventAlertParameterKey, setEventAlertParameterKey] = useState(null);
    const [eventAlertParameterValue, setEventAlertParameterValue] = useState(null);
    const [eventAlertParameters, setEventAlertParameters] = useState([]);
    const [eventParameters, setEventParameters] = useState([]);

    const [showDialog, setShowDialog] = useState(false);
    const [dialogVenueEventAlertKey, setDialogVenueEventAlertKey] = useState(null);

    const getVenueEventAlertTypes = () => {
        fetch(`/api/general/venueeventalerttypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setVenueEventAlertTypes(result);

                getVenueEventAlerts(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventAlertsLoading(false);
                setLoadingError(error.message);
            });
    }

    const getTicketCountInputParameters = () => {
        const dataItem = {
            ticketingWebsiteId: props.ticketingWebsiteId
        }

        fetch(`/api/general/ticketcountinputparameters`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setTicketCountInputParameters(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventParameters = () => {
        const dataItem = {
            venueEventKey: props.venueEventKey
        }

        fetch(`/api/administrator/venueeventparameters`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setEventParameters(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventAlerts = (localVenueEventAlertTypes) => {        
        setAreVenueEventAlertsLoading(true);

        if (!localVenueEventAlertTypes) {
            localVenueEventAlertTypes = venueEventAlertTypes;
        }

        const dataItem = {
            venueEventKey: props.venueEventKey
        }

        fetch(`/api/administrator/venueeventalerts`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setAreVenueEventAlertsLoading(false);

                let venueEventAlertsCopy = result;
                venueEventAlertsCopy.forEach(element => {
                    if (element.venueEventAlertTypeId) {
                        element.venueEventAlertType = localVenueEventAlertTypes.find((p) => p.venueEventAlertTypeId === element.venueEventAlertTypeId);
                    }
                });

                setVenueEventAlerts(venueEventAlertsCopy);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventAlertsLoading(false);
                setLoadingError(error.message);
            });
    }

    const addVenueEventAlert = (dataItem) => {
        dataItem.venueEventAlertTypeId = dataItem.venueEventAlertType.venueEventAlertTypeId;
        dataItem.venueEventAlertParameters = eventAlertParameters;

        fetch("/api/administrator/venueeventalert",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setOpenForm(false);

                addNotificationWithTimeout(dispatch, "success", "Created item successfully");

                getVenueEventAlerts();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const updateVenueEventAlert = (dataItem) => {
        dataItem.venueEventAlertTypeId = dataItem.venueEventAlertType.venueEventAlertTypeId;
        dataItem.venueEventAlertParameters = eventAlertParameters;

        if (dataItem.ticketCount === "") {
            dataItem.ticketCount = null;
        }

        fetch("/api/administrator/venueeventalert",
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setOpenForm(false);

                addNotificationWithTimeout(dispatch, "success", "Updated item successfully");

                getVenueEventAlerts();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const deleteVenueEventAlert = (venueEventAlertKey) => {
        const dataItem = {
            venueEventAlertKey: venueEventAlertKey
        }

        fetch("/api/administrator/venueeventalert",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getVenueEventAlerts();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getCurrentTicketCount = (venueEventAlertKey) => {
        setIsLoading(true);

        const dataItem = {
            venueEventAlertKey: venueEventAlertKey
        }

        fetch("/api/administrator/ticketcount",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                let venueEventAlertsCopy = venueEventAlerts;
                const index = venueEventAlertsCopy.findIndex(x => x.venueEventAlertKey === venueEventAlertKey);
                venueEventAlertsCopy[index].currentTicketCount = result.ticketCount;

                setVenueEventAlerts(venueEventAlertsCopy);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            edit={enterEdit}
            remove={remove}
        />
    );

    const GetTicketCountCell = (props) => {
        const key = props.dataItem.venueEventAlertKey;
        const currentTicketCount = props.dataItem.currentTicketCount;

        return (
            <td>
                { currentTicketCount !== undefined ? currentTicketCount : <Button fillMode="link" type={"button"} onClick={() => getCurrentTicketCount(key)}>Get</Button> }                
            </td>
        );
    };

    const ViewLogCell = (props) => {  
        const key = props.dataItem.venueEventAlertKey;
        
        return (
            <td>
                <Button fillMode="link" type={"button"} onClick={() => viewLog(key)}>View</Button> 
            </td>
        );
    };

    const viewLog = (venueEventAlertKey) => {
        setDialogVenueEventAlertKey(venueEventAlertKey);
        setShowDialog(true);
    }

    const remove = (dataItem) => {
        deleteVenueEventAlert(dataItem.venueEventAlertKey);
    };

    const save = (dataItem) => {        
        //Perform validation  
        if (dataItem.venueEventAlertType) {
            dataItem.venueEventAlertTypeId = dataItem.venueEventAlertType.venueEventAlertTypeId;
        }

        if (dataItem.venueEventAlertKey) {
            updateVenueEventAlert(dataItem);
        }
        else {
            addVenueEventAlert(dataItem);
        }
    }

    const cancel = (dataItem) => {
        setOpenForm(false);
    };

    const enterEdit = (dataItem) => {
        setEditItem(dataItem);
        setEventAlertParameters(dataItem.venueEventAlertParameters);
        setOpenForm(true);
    };

    const addNew = () => {
        setEditItem({
            venueEventKey: props.venueEventKey,
            isActive: true,
        });
        setEventAlertParameters([]);
        setOpenForm(true);
    };

    const addEventAlertParameterKeyFromInput = (event) => {
        setEventAlertParameterKey(event.value);
    };

    const addEventAlertParameterKeyFromDropDownList = (event) => {
        setEventAlertParameterKey(event.value.name);
    };

    const addEventAlertParameterValue = (event) => {
        setEventAlertParameterValue(event.value);
    };

    const addEventAlertParameterWithoutInputs = () => {
        addEventAlertParameter(eventAlertParameterKey, eventAlertParameterValue);
    };

    const addEventAlertParameterWithInputs = (localEventAlertParameterKey, localEventAlertParameterValue) => {
        addEventAlertParameter(localEventAlertParameterKey, localEventAlertParameterValue);
    };

    const addEventAlertParameter = (localEventAlertParameterKey, localEventAlertParameterValue) => {
        if (!localEventAlertParameterKey
            || !localEventAlertParameterValue) {
            addNotificationWithTimeout(dispatch, "error", "The alert parameter key and value are required.");
            return;
        }

        const existingParameter = eventAlertParameters.find(element => element.key === localEventAlertParameterKey);

        if (existingParameter) {
            addNotificationWithTimeout(dispatch, "error", "A parameter already exists with that key.");
            return;
        }

        const newEventAlertParameters = [...eventAlertParameters];
        newEventAlertParameters.push({
            key: localEventAlertParameterKey,
            value: localEventAlertParameterValue
        });
        setEventAlertParameters(newEventAlertParameters);
    };

    const removeEventAlertParameter = (key) => {
        const existingParameter = eventAlertParameters.find(element => element.key === key);

        if (!existingParameter) {
            addNotificationWithTimeout(dispatch, "error", "No parameter found with that key.");
            return;
        }

        const indexOfItem = eventAlertParameters.indexOf(existingParameter);

        const newEventAlertParameters = [...eventAlertParameters];
        newEventAlertParameters.splice(indexOfItem, 1);
        setEventAlertParameters(newEventAlertParameters);
    }

    const EventAlertParameterRender = (props) => {
        const item = props.dataItem;

        return (
            <div className="mb-2">
                {item.key}: {item.value} 
                &nbsp;&nbsp;
                <Button type={"button"} onClick={() => removeEventAlertParameter(item.key)} icon="close" fillMode="outline" />
            </div>
        );
    };

    const EventParameterRender = (props) => {
        const item = props.dataItem;

        return (
            <div className="mb-2">
                {item.key}: {item.value} 
                &nbsp;&nbsp;
                <Button type={"button"} onClick={() => addEventAlertParameterWithInputs(item.key, item.value)} icon="plus" fillMode="outline" />
            </div>
        );
    };

    useEffect(() => {
        getVenueEventParameters();
        getVenueEventAlertTypes();
        getTicketCountInputParameters();
    }, []);

    return (
        <>
            { areVenueEventAlertsLoading ? <LoadingPanel /> : (loadingError === "" ? 
                <>
                    { isLoading ? <LoadingPanel /> : "" }

                    <Grid
                        data={venueEventAlerts}
                        dataItemKey={"venueEventAlertKey"}
                        resizable={true}
                        scrollable="none"
                    >
                        <GridToolbar>
                            <Button
                                title={"Add New"}
                                themeColor={"primary"}
                                icon="plus"
                                onClick={addNew}
                            >
                                Add New
                            </Button>
                        </GridToolbar>                        
                        <Column field="venueEventAlertTypeName" title={"Type"} />                        
                        <Column field="sectionName" title={"Section Name"} />
                        <Column field="ticketCount" title={"Trigger Count"} /> 
                        <Column field="isActive" title={"Active"} />
                        <Column field="dateCreated" title={"Date Created"} />
                        <Column title={"Count"} cell={GetTicketCountCell} />
                        <Column title={"Log"} cell={ViewLogCell} />                        
                        <Column cell={MyCommandCell} width="200px" />
                    </Grid>
                    {openForm && 
                        <AdministratorVenueEventAlertForm
                            venueEventAlertTypes={venueEventAlertTypes}
                            ticketCountInputParameters={ticketCountInputParameters}
                            cancel={cancel}
                            save={save}
                            item={editItem}
                            eventParameters={eventParameters}
                            eventAlertParameters={eventAlertParameters}
                            eventAlertParameterRender={EventAlertParameterRender}
                            eventParameterRender={EventParameterRender}
                            addEventAlertParameterWithoutInputs={addEventAlertParameterWithoutInputs}
                            addEventAlertParameterKeyFromInput={addEventAlertParameterKeyFromInput}
                            addEventAlertParameterKeyFromDropDownList={addEventAlertParameterKeyFromDropDownList}
                            addEventAlertParameterValue={addEventAlertParameterValue}
                        />
                    }
                </>
                : <></>)
            }
            {showDialog === false ? <></> :
                <Dialog
                    title={"Alert Log"}
                    onClose={() => setShowDialog(false)}
                    width={"90vw"}
                >
                    <AdministratorVenueEventAlertLogs venueEventAlertKey={dialogVenueEventAlertKey} />
                </Dialog>
            }
        </>
    );
}